import React, { useState, useCallback } from "react";
import { Modal, Button } from "react-bootstrap";
import HierarchicalMenu from "../menus/HierarchicalMenu";
import LoadingSpinner from "../common/LoadingSpinner";
import "../../styles/components/FilterModal.css";
import GeneNamesForm from "../forms/GeneNamesForm";
import { callOperation } from "../../utils/apiHelper.js";

const DiseasesModal = ({
  showModal,
  handleClose,
  hierarchy,
  selectedDiseases,
  setSelectedDiseases,
  confidence_score,
  setConfidenceScore,
  DiseaseDatasets,
  setDiseaseDatasets,
  filterData,
  setFilterData,
  token,
}) => {
  const [showGeneNames, setShowGeneNames] = useState(false); // Control whether to show gene names
  const [editedGeneNames, setEditedGeneNames] = useState(""); // Store the edited gene names
  const [loading, setLoading] = useState(false); // Loading state for Apply button

  // Use geneNames and to update the filterData
  const handleAddGeneNames = () => {
    let temp = { ...filterData };
    if (editedGeneNames.length > 0) {
      temp["feature"] = temp["feature"]
        ? temp["feature"] + "," + editedGeneNames
        : editedGeneNames;
      setFilterData(temp);

      // Reset the editedGeneNames
      setEditedGeneNames("");
    }
    setShowGeneNames(false);
    handleClose();
  };

  const ApplyDiseases = useCallback(async () => {
    const doids = selectedDiseases.map((item) => item.id);
    setLoading(true); // Start loading
    // wait 4 second to simulate a network request
    // await new Promise(resolve => setTimeout(resolve, 4000));
    try {
      const response = await callOperation("mapdoid", {
        doids: doids,
        min_confidence: parseFloat(confidence_score),
        datasets: DiseaseDatasets,
      }, token);

      // Extract gene names and remove duplicates
      const geneNames = [
        ...new Set(response.data.map((item) => item["gene name"])),
      ];

      // update editedGeneNames
      setEditedGeneNames(geneNames.join(", "));

      // Switch to showing gene names inside the modal
      setShowGeneNames(true);
    } catch (error) {
      console.error("Failed to apply diseases:", error);
    } finally {
      setLoading(false); // Stop loading
    }
  }, [selectedDiseases, confidence_score, DiseaseDatasets, token]);

  return (
    <Modal
      show={showModal}
      onHide={handleClose}
      size="lg"
      className="filter-modal unselectable-all bounce-in"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          {showGeneNames
            ? "Gene Names from Selected Diseases"
            : "Select Diseases"}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="filter-modal-body">
          {showGeneNames ? (
            // Show editable gene names with loading state
            <GeneNamesForm
              editedGeneNames={editedGeneNames}
              setEditedGeneNames={setEditedGeneNames}
              loading={loading}
            />
          ) : (
            // Show the disease selection menu, disable during loading
            <HierarchicalMenu
              data={hierarchy}
              selectedDiseases={selectedDiseases}
              setSelectedDiseases={setSelectedDiseases}
              confidence_score={confidence_score}
              setConfidenceScore={setConfidenceScore}
              DiseaseDatasets={DiseaseDatasets}
              setDiseaseDatasets={setDiseaseDatasets}
              disable={loading} // Disable HierarchicalMenu when loading
            />
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {showGeneNames ? (
          // If showing gene names, allow user to go back or add gene names
          <>
            <Button
              variant="success"
              className="btn-animate"
              onClick={handleAddGeneNames}
              disabled={loading} // Disable Add button when loading
            >
              Add
            </Button>
            <Button
              variant="primary"
              className="btn-animate"
              onClick={() => setShowGeneNames(false)}
              disabled={loading} // Disable Back button when loading
            >
              Back
            </Button>
          </>
        ) : (
          // If showing disease selection, show Apply and Close buttons
          <>
            <Button
              variant="primary"
              className="btn-animate d-flex align-items-center justify-content-center gap-2"
              onClick={() => {
                ApplyDiseases();
              }}
              disabled={
                loading ||
                selectedDiseases.length === 0 ||
                DiseaseDatasets.length === 0
              } // Disable when loading or conditions not met
              title={
                selectedDiseases.length === 0 || DiseaseDatasets.length === 0
                  ? "Please select diseases and datasets first"
                  : ""
              }
            >
              {loading ? (
                <>
                  <LoadingSpinner /> Applying...
                </>
              ) : (
                "Apply"
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default DiseasesModal;
